import React, { useState, useEffect } from "react";
import { User, Edit2, Loader2, ChevronDown } from "lucide-react";
import {
  getMyProfile,
  updateMyProfile,
  uploadProfileImage,
} from "../../../api/user-service/userService";
import Logger from "../../../utils/logger";
import { useAuth0 } from "@auth0/auth0-react";
import ProfileStyle from "../../../assets/stylesheets/dashboard/profile.module.css";
import DashboardLayout from "../DashboardLayout";
import useAuthStore from "../../../store/authStore";

const log = Logger.create("ProfilePage");

export default function ProfilePage() {
  const { user } = useAuth0();
  const { setUserProfile } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    displayName: "",
    dateOfBirth: "",
    phoneNumber: "",
    address: {
      street1: "",
      street2: "",
      city: "",
      province: "",
      zipcode: "",
      country: "",
    },
  });

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const res = await getMyProfile();

      // Backend may return either user object directly or ApiResponseDto { success, data }
      const profile = res?.data ?? res;
      log.debug("Fetched profile", profile);

      if (!profile) return;

      // Shared source of truth for sidebar/user header across dashboard pages
      setUserProfile(profile);

      setFormData((prev) => ({
        ...prev,
        firstName: profile.firstName ?? "",
        lastName: profile.lastName ?? "",
        phoneNumber: profile.phoneNumber ?? "",
        // displayName/address might not exist depending on backend response; keep empty if missing
        displayName: profile.displayName ?? "",
        dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.split("T")[0] : "",
        address: {
          street1: profile.address?.street1 ?? "",
          street2: profile.address?.street2 ?? "",
          city: profile.address?.city ?? "",
          province: profile.address?.province ?? "",
          zipcode: profile.address?.zipcode ?? "",
          country: profile.address?.country ?? "",
        },
      }));

      if (profile.profileImage) setImagePreview(profile.profileImage);
    } catch (error) {
      if (error?.response?.status === 404) {
        log.info("Profile not found yet; showing empty form");
        return;
      }
      log.error("Failed to fetch profile", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const objectUrl = URL.createObjectURL(file);
      setImagePreview(objectUrl);

      const updatedProfile = await uploadProfileImage(file);
      log.info("Profile image updated");

      if (updatedProfile?.profileImage) setImagePreview(updatedProfile.profileImage);
    } catch (error) {
      log.error("Failed to upload image", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);

      // Backend DTO allows only a subset. Do not send displayName/address.
      const digitsOnlyPhone = (formData.phoneNumber || "").replace(/\D/g, "");

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        ...(digitsOnlyPhone ? { phoneNumber: digitsOnlyPhone } : {}),
        ...(formData.dateOfBirth ? { dateOfBirth: formData.dateOfBirth } : {}),
      };

      const updated = await updateMyProfile(payload);
      log.info("Profile updated successfully");

      if (updated) {
        // keep store in sync so sidebar updates immediately everywhere
        setUserProfile(updated?.data ?? updated);

        setFormData((prev) => ({
          ...prev,
          firstName: updated.firstName ?? prev.firstName,
          lastName: updated.lastName ?? prev.lastName,
          phoneNumber: updated.phoneNumber ?? prev.phoneNumber,
          dateOfBirth: updated.dateOfBirth
            ? updated.dateOfBirth.split("T")[0]
            : prev.dateOfBirth,
          // displayName/address are managed locally for now (backend doesn't accept them in PATCH)
        }));
        if (updated.profileImage) setImagePreview(updated.profileImage);
      }

      alert("Profile updated successfully!");
    } catch (error) {
      log.error("Failed to update profile", error);
      alert("Failed to update profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={ProfileStyle["loading-container"]}>
        <Loader2 className={ProfileStyle["spinner"]} size={32} />
      </div>
    );
  }

  return (
    <DashboardLayout>
      <form onSubmit={handleSubmit}>
        <div className={ProfileStyle["section"]}>
          <h2 className={ProfileStyle["section-title"]}>Basic Information</h2>

          <div className={ProfileStyle["profile-upload-area"]}>
            <div className={ProfileStyle["profile-image-container"]}>
              <div className={ProfileStyle["large-avatar-wrapper"]}>
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Profile"
                    className={ProfileStyle["avatar-image"]}
                  />
                ) : (
                  <div className={ProfileStyle["avatar-placeholder"]}>
                    <User size={40} />
                  </div>
                )}
              </div>
              <label
                htmlFor="profile-upload"
                className={ProfileStyle["edit-icon-label"]}
              >
                <Edit2 size={14} />
              </label>
              <input
                id="profile-upload"
                type="file"
                accept="image/*"
                className={ProfileStyle["hidden-input"]}
                onChange={handleImageUpload}
              />
            </div>
            <div>
              <h3 className={ProfileStyle["user-name"]}>
                {formData.firstName} {formData.lastName}
              </h3>
            </div>
          </div>

          <div className={ProfileStyle["form-grid"]}>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
                required
              />
            </div>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
                required
              />
            </div>

            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>
                Displayed Name (Optional)
              </label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
              />
            </div>

            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Date of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
              />
            </div>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Phone Number</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
              />
            </div>
          </div>
        </div>

        <div className={ProfileStyle["section"]}>
          <h2 className={ProfileStyle["section-title"]}>Address</h2>
          <div className={ProfileStyle["form-grid"]}>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Street 1</label>
              <input
                type="text"
                name="address.street1"
                value={formData.address.street1}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
              />
            </div>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Street 2</label>
              <input
                type="text"
                name="address.street2"
                value={formData.address.street2}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
              />
            </div>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>City</label>
              <input
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
              />
            </div>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Province</label>
              <div className={ProfileStyle["select-wrapper"]}>
                <select
                  name="address.province"
                  value={formData.address.province}
                  onChange={handleInputChange}
                  className={ProfileStyle["select"]}
                >
                  <option value="">Select Province</option>
                  <option value="ON">Ontario</option>
                  <option value="BC">British Columbia</option>
                  <option value="QC">Quebec</option>
                  <option value="AB">Alberta</option>
                  <option value="MB">Manitoba</option>
                  <option value="NS">Nova Scotia</option>
                  <option value="NB">New Brunswick</option>
                  <option value="PE">Prince Edward Island</option>
                  <option value="SK">Saskatchewan</option>
                  <option value="NL">Newfoundland and Labrador</option>
                </select>
                <ChevronDown
                  className={ProfileStyle["select-icon"]}
                  size={20}
                />
              </div>
            </div>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Zip Code</label>
              <input
                type="text"
                name="address.zipcode"
                value={formData.address.zipcode}
                onChange={handleInputChange}
                className={ProfileStyle["input"]}
              />
            </div>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Country</label>
              <div className={ProfileStyle["select-wrapper"]}>
                <select
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleInputChange}
                  className={ProfileStyle["select"]}
                >
                  <option value="">Select Country</option>
                  <option value="Canada">Canada</option>
                </select>
                <ChevronDown
                  className={ProfileStyle["select-icon"]}
                  size={20}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={ProfileStyle["form-footer"]}>
          <button
            type="submit"
            disabled={isSaving}
            className={ProfileStyle["save-button"]}
          >
            {isSaving ? (
              <>
                <Loader2
                  className={`${ProfileStyle["spinner"]} inline mr-2`}
                  size={18}
                />
                Saving...
              </>
            ) : (
              "Save Profile"
            )}
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
}
